<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEditMode ? '編輯程式' : '新增程式' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel">
          取消
        </button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting">
            <i class="ri-loader-4-line spin"></i>
            {{ isEditMode ? '更新中...' : '建立中...' }}
          </span>
          <span v-else>
            {{ isEditMode ? '更新程式' : '建立程式' }}
          </span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="create-program-container">
    <div class="form-card">
      <form @submit.prevent="handleSubmit">
        <div class="form-row">
          <div class="form-group">
            <label class="required">程式代碼</label>
            <input
              v-model="form.programCode"
              type="text"
              class="form-control"
              placeholder="請輸入程式代碼"
              :disabled="isEditMode"
              @input="form.programCode = form.programCode.toUpperCase()"
              required
            />
            <small class="form-text">唯一識別碼，建議使用大寫英文與底線，例如：USER_LIST</small>
            <small v-if="isEditMode" class="form-text text-muted">編輯時無法更改程式代碼</small>
          </div>

          <div class="form-group">
            <label class="required">程式名稱</label>
            <input
              v-model="form.programName"
              type="text"
              class="form-control"
              placeholder="請輸入程式名稱"
              required
            />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group full-width">
            <label>程式路徑</label>
            <input
              v-model="form.programPath"
              type="text"
              class="form-control"
              placeholder="例如：/system/users"
            />
            <small class="form-text">前端路由路徑</small>
          </div>
        </div>

        <div v-if="isEditMode" class="info-box">
          <i class="ri-information-line"></i>
          <div class="info-content">
            <strong>模組關聯管理</strong>
            <p>此程式可以在多個模組中使用。請到<router-link to="/system/modules" class="link">模組管理</router-link>頁面，編輯對應的模組來添加或移除程式關聯。</p>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group full-width">
            <label>權限設定</label>
            <div class="permission-selector">
              
              <!-- 資源名稱輸入 -->
              <div class="resource-input-group">
                <label class="sub-label">資源名稱</label>
                <input
                  v-model="resourceName"
                  type="text"
                  class="form-control"
                  placeholder="自動從程式代碼轉換"
                  disabled
                />
                <small class="form-text">
                  自動從程式代碼轉換為小寫，無需手動輸入
                </small>
              </div>

              <!-- 操作權限多選 -->
              <div class="actions-selector">
                <label class="sub-label">可用操作</label>
                <small class="form-text actions-hint">
                  選擇此程式提供的功能操作，系統會根據選擇的操作生成對應的權限代碼。例如：選擇「新增」和「查詢」會生成「資源名稱:create」和「資源名稱:read」權限代碼
                </small>
                <div class="checkbox-grid">
                  <label class="checkbox-item">
                    <input type="checkbox" value="create" v-model="selectedActions" @change="updatePermissions" />
                    <i class="ri-add-line"></i>
                    <span>新增 (create)</span>
                  </label>
                  <label class="checkbox-item">
                    <input 
                      type="checkbox" 
                      value="read" 
                      v-model="selectedActions" 
                      @change="updatePermissions"
                      :disabled="selectedActions.length === 1 && selectedActions.includes('read')"
                    />
                    <i class="ri-eye-line"></i>
                    <span>查詢 (read) <small style="color: #dc3545;">必選</small></span>
                  </label>
                  <label class="checkbox-item">
                    <input type="checkbox" value="update" v-model="selectedActions" @change="updatePermissions" />
                    <i class="ri-edit-line"></i>
                    <span>編輯 (update)</span>
                  </label>
                  <label class="checkbox-item">
                    <input type="checkbox" value="delete" v-model="selectedActions" @change="updatePermissions" />
                    <i class="ri-delete-bin-line"></i>
                    <span>刪除 (delete)</span>
                  </label>
                  <label class="checkbox-item">
                    <input type="checkbox" value="export" v-model="selectedActions" @change="updatePermissions" />
                    <i class="ri-file-download-line"></i>
                    <span>匯出 (export)</span>
                  </label>
                  <label class="checkbox-item">
                    <input type="checkbox" value="print" v-model="selectedActions" @change="updatePermissions" />
                    <i class="ri-printer-line"></i>
                    <span>列印 (print)</span>
                  </label>
                </div>
                <div class="actions-description">
                  <div class="action-desc-item">
                    <strong>新增 (create)</strong>：允許用戶建立新資料，對應「新增」按鈕的顯示與功能
                  </div>
                  <div class="action-desc-item">
                    <strong>查詢 (read)</strong>：允許用戶查看和瀏覽資料，對應列表頁面和詳情頁面的訪問權限（必選）
                  </div>
                  <div class="action-desc-item">
                    <strong>編輯 (update)</strong>：允許用戶修改現有資料，對應「編輯」按鈕的顯示與功能
                  </div>
                  <div class="action-desc-item">
                    <strong>刪除 (delete)</strong>：允許用戶刪除資料，對應「刪除」按鈕的顯示與功能
                  </div>
                  <div class="action-desc-item">
                    <strong>匯出 (export)</strong>：允許用戶匯出資料，對應「匯出」按鈕的顯示與功能
                  </div>
                  <div class="action-desc-item">
                    <strong>列印 (print)</strong>：允許用戶列印資料，對應「列印」按鈕的顯示與功能
                  </div>
                </div>
              </div>

              <!-- 權限預覽 -->
              <div class="permission-preview">
                <label class="sub-label">生成的權限代碼</label>
                <div class="permission-tags">
                  <span 
                    v-for="perm in generatedPermissions" 
                    :key="perm"
                    class="permission-tag"
                    :class="{ 'read-only': perm.endsWith(':read') }"
                  >
                    {{ perm }}
                    <i 
                      v-if="!perm.endsWith(':read')" 
                      class="ri-close-line" 
                      @click="removePermission(perm)"
                    ></i>
                    <i 
                      v-else 
                      class="ri-lock-line" 
                      style="cursor: default; opacity: 0.6;"
                      title="read 權限不可移除"
                    ></i>
                  </span>
                  <span v-if="generatedPermissions.length === 0" class="empty-hint">
                    請選擇資源名稱和操作權限
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>排序順序</label>
            <input
              v-model.number="form.sortOrder"
              type="number"
              class="form-control"
              placeholder="數字越小越前面"
              min="0"
            />
          </div>

          <div class="form-group"></div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>圖示</label>
            <IconPicker v-model="form.icon" />
            <small class="form-text">選擇程式顯示的圖示</small>
          </div>

          <div class="form-group">
            <label>狀態</label>
            <select v-model="form.status" class="form-control">
              <option value="active">啟用</option>
              <option value="inactive">停用</option>
            </select>
            <p class="form-hint">
              <strong>⚠️ 停用程式的影響：</strong><br>
              • 不會出現在選單中，用戶無法看到此程式<br>
              • 不會出現在程式選單 API 的返回結果中<br>
              • 即使已分配給模組，也不會顯示在該模組的程式列表中
            </p>
          </div>
        </div>

        <div class="form-group">
          <label>描述</label>
          <textarea
            v-model="form.description"
            class="form-control"
            rows="3"
            placeholder="請輸入程式描述"
          ></textarea>
        </div>

      </form>
    </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { programService } from '@/api/modules/programs.js';
import { showSuccess, showError } from '@/utils/swal.js';
import { normalizePermissions } from '@/utils/permissions.js';
import IconPicker from '@/components/IconPicker.vue';

const VALID_PERMISSION_ACTIONS = new Set([
  'create',
  'read',
  'update',
  'delete',
  'export',
  'print'
]);

export default {
  name: 'CreateProgram',
  components: {
    IconPicker
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const submitting = ref(false);
    const showPermissionHelp = ref(false);
    
    const form = ref({
      programCode: '',
      programName: '',
      programPath: '',
      permissions: [],
      icon: '',
      sortOrder: 0,
      status: 'active',
      description: ''
    });

    // 權限選擇相關
    const resourceName = ref('');
    // ⚠️ 重要：read 權限預設勾選，因為沒有 read 權限就連程式都看不見
    const selectedActions = ref(['read']);
    const generatedPermissions = computed(() => {
      if (!resourceName.value || selectedActions.value.length === 0) {
        return [];
      }
      return selectedActions.value.map(action => `${resourceName.value}:${action}`);
    });

    // 更新 permissions
    const updatePermissions = () => {
      form.value.permissions = generatedPermissions.value;
    };

    // 移除單個權限
    const removePermission = (perm) => {
      const [resource, action] = perm.split(':');
      // ⚠️ 重要：read 權限不可移除，因為沒有 read 權限就連程式都看不見
      if (action === 'read') {
        return;
      }
      const actionIndex = selectedActions.value.indexOf(action);
      if (actionIndex > -1) {
        selectedActions.value.splice(actionIndex, 1);
      }
      updatePermissions();
    };

    const isEditMode = computed(() => !!route.params.id);

    const fetchProgram = async () => {
      try {
        const response = await programService.getProgram(route.params.id);
        if (response && response.success && response.data) {
          const program = response.data;
          const normalizedPerms = normalizePermissions(program.permissions || []);

          form.value = {
            programCode: program.programCode || '',
            programName: program.programName || '',
            programPath: program.programPath || '',
            permissions: normalizedPerms,
            icon: program.icon || '',
            sortOrder: program.sortOrder || 0,
            status: program.status || 'active',
            description: program.description || ''
          };

          // 資源名稱始終從程式代碼轉換為小寫
          resourceName.value = (program.programCode || '').toLowerCase();

          // 從權限字串還原已勾選操作（取最後一段，支援舊三段式經 normalize 後的二段式）
          if (normalizedPerms.length > 0) {
            const actions = [
              ...new Set(
                normalizedPerms
                  .map((p) => {
                    if (!p || typeof p !== 'string' || !p.includes(':')) return null;
                    const last = p.split(':').pop();
                    return VALID_PERMISSION_ACTIONS.has(last) ? last : null;
                  })
                  .filter(Boolean)
              )
            ];
            selectedActions.value = actions.length ? actions : ['read'];
            if (!selectedActions.value.includes('read')) {
              selectedActions.value.push('read');
            }
          } else {
            selectedActions.value = ['read'];
          }

          updatePermissions();
        }
      } catch (error) {
        console.error('獲取程式資料失敗:', error);
        showError('錯誤', '獲取程式資料失敗');
        router.push({ name: 'ProgramList', query: route.query });
      }
    };

    // 監聽程式代碼變化，自動更新資源名稱
    watch(() => form.value.programCode, (newCode) => {
      if (newCode) {
        resourceName.value = newCode.toLowerCase();
        updatePermissions();
      }
    });

    const validateForm = () => {
      if (!form.value.programCode || !form.value.programCode.trim()) {
        showError('驗證失敗', '請輸入程式代碼');
        return false;
      }

      if (!form.value.programName || !form.value.programName.trim()) {
        showError('驗證失敗', '請輸入程式名稱');
        return false;
      }

      return true;
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      submitting.value = true;

      try {
        const programData = {
          programName: form.value.programName.trim(),
          programPath: form.value.programPath ? form.value.programPath.trim() : null,
          permissions: normalizePermissions(form.value.permissions || []),
          icon: form.value.icon ? form.value.icon.trim() : null,
          sortOrder: form.value.sortOrder || 0,
          status: form.value.status,
          description: form.value.description ? form.value.description.trim() : null
        };

        // 新增時需要額外的欄位
        if (!isEditMode.value) {
          programData.programCode = form.value.programCode.trim();
        }

        let response;
        if (isEditMode.value) {
          response = await programService.updateProgram(route.params.id, programData);
        } else {
          response = await programService.createProgram(programData);
        }

        if (response && response.success) {
          await showSuccess(
            isEditMode.value ? '更新成功' : '建立成功',
            `程式「${form.value.programName}」已成功${isEditMode.value ? '更新' : '建立'}${!isEditMode.value ? '，請到模組管理頁面添加此程式到相應模組' : ''}`
          );
          router.push({ name: 'ProgramList', query: route.query });
        } else {
          const errorMsg = response?.message || `${isEditMode.value ? '更新' : '建立'}程式失敗`;
          showError(isEditMode.value ? '更新失敗' : '建立失敗', errorMsg);
        }
      } catch (error) {
        console.error(`${isEditMode.value ? '更新' : '建立'}程式失敗:`, error);
        let errorMsg = error.message || `${isEditMode.value ? '更新' : '建立'}程式失敗，請稍後再試`;
        
        if (error.code === 'PROGRAM_CONFLICT_001') {
          errorMsg = '程式代碼已存在，請使用其他代碼';
        }
        
        showError(isEditMode.value ? '更新失敗' : '建立失敗', errorMsg);
      } finally {
        submitting.value = false;
      }
    };

    const handleCancel = () => {
      router.push({ name: 'ProgramList', query: route.query });
    };

    const copyToClipboard = async (text) => {
      // This function is no longer used in the new UI
    };

    onMounted(async () => {
      if (isEditMode.value) {
        await fetchProgram();
      }
    });

    return {
      form,
      submitting,
      isEditMode,
      resourceName,
      selectedActions,
      generatedPermissions,
      updatePermissions,
      removePermission,
      handleSubmit,
      handleCancel
    };
  }
};
</script>

<style scoped>
.create-program-container {
  padding: 0;
}

.page-header {
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.form-card {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.info-box {
  background: #e7f3ff;
  border-left: 4px solid #2196f3;
  padding: 16px;
  margin-bottom: 20px;
  border-radius: 4px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.info-box i {
  font-size: 20px;
  color: #2196f3;
  margin-top: 2px;
}

.info-content {
  flex: 1;
}

.info-content strong {
  display: block;
  margin-bottom: 4px;
  color: #1976d2;
}

.info-content p {
  margin: 0;
  color: #555;
  line-height: 1.5;
}

.info-content .link {
  color: #2196f3;
  text-decoration: underline;
  font-weight: 500;
}

.info-content .link:hover {
  color: #1976d2;
}

/* 權限代碼輸入與說明按鈕 */
.input-with-help {
  display: flex;
  gap: 8px;
}

.input-with-help .form-control {
  flex: 1;
}

.help-button {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  color: #666;
}

.help-button:hover {
  background: #f5f5f5;
  border-color: #2196f3;
  color: #2196f3;
}

.help-button.active {
  background: #2196f3;
  border-color: #2196f3;
  color: white;
}

.help-button i {
  font-size: 18px;
}

/* 權限說明框 */
.permission-help-box {
  margin-top: 12px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.help-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #2196f3;
}

.help-header i {
  font-size: 20px;
  color: #2196f3;
}

.help-header strong {
  font-size: 16px;
  color: #333;
}

.help-section {
  margin-bottom: 20px;
}

.help-section:last-child {
  margin-bottom: 0;
}

.help-section h4 {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
}

.help-text {
  font-size: 13px;
  color: #666;
  margin: 8px 0 0 0;
  line-height: 1.5;
}

.code-format {
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 12px;
  margin: 8px 0;
}

.code-format code {
  color: #d63384;
  font-size: 14px;
  font-weight: 500;
}

/* 權限表格 */
.permission-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
  background: white;
  border-radius: 4px;
  overflow: hidden;
}

.permission-table th {
  background: #2196f3;
  color: white;
  padding: 10px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
}

.permission-table td {
  padding: 10px;
  border-bottom: 1px solid #e9ecef;
  font-size: 13px;
}

.permission-table tr:last-child td {
  border-bottom: none;
}

.permission-table tr:hover td {
  background: #f8f9fa;
}

.permission-table code {
  background: #f1f3f5;
  color: #d63384;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 500;
}

/* 範例列表 */
.example-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
  margin-top: 8px;
}

.example-item {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: all 0.2s;
  position: relative;
}

.example-item.clickable {
  cursor: pointer;
  padding-right: 36px; /* 為複製圖示留空間 */
}

.example-item.clickable:hover {
  border-color: #2196f3;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.2);
  transform: translateY(-1px);
  background: #f8f9ff;
}

.example-item.clickable:active {
  transform: translateY(0);
  box-shadow: 0 1px 4px rgba(33, 150, 243, 0.3);
}

.example-item code {
  color: #d63384;
  font-size: 13px;
  font-weight: 600;
}

.example-desc {
  font-size: 12px;
  color: #666;
}

.copy-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  color: #999;
  transition: all 0.2s;
}

.example-item.clickable:hover .copy-icon {
  color: #2196f3;
  transform: translateY(-50%) scale(1.1);
}

/* 提示文字 */
.help-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  background: #fff8e1;
  border-left: 3px solid #ffc107;
  border-radius: 4px;
  font-size: 13px;
  color: #856404;
}

.help-tip i {
  font-size: 16px;
  color: #ffc107;
}

/* 注意事項列表 */
.notice-list {
  margin: 8px 0 0 0;
  padding-left: 20px;
}

.notice-list li {
  font-size: 13px;
  color: #666;
  line-height: 1.8;
  margin-bottom: 4px;
}

.notice-list code {
  background: #f1f3f5;
  color: #d63384;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 12px;
}


.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.form-control:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.form-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #6c757d;
}

.form-text.text-muted {
  color: #dc3545;
  font-style: italic;
}

.form-hint {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  margin-bottom: 0;
}

.form-checkbox {
  margin-right: 8px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #dee2e6;
}

.btn {
  padding: 10px 24px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #545b62;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 權限選擇器樣式 */
.permission-selector {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background: #f9f9f9;
}

.resource-input-group {
  margin-bottom: 20px;
}

.sub-label {
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
}

.actions-selector {
  margin-bottom: 20px;
}

.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.checkbox-item:hover {
  border-color: #007bff;
  background: #f0f8ff;
}

.checkbox-item input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.checkbox-item i {
  font-size: 18px;
  color: #666;
}

.checkbox-item input[type="checkbox"]:checked ~ i {
  color: #007bff;
}

.permission-preview {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 16px;
}

.permission-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 40px;
}

.permission-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #007bff;
  color: white;
  border-radius: 4px;
  font-size: 13px;
  font-family: monospace;
}

.permission-tag.read-only {
  background: #28a745;
}

.permission-tag i {
  cursor: pointer;
  font-size: 14px;
}

.permission-tag i:hover {
  color: #ffdddd;
}

.empty-hint {
  color: #999;
  font-style: italic;
  padding: 8px 0;
}

.actions-hint {
  display: block;
  margin-top: 4px;
  margin-bottom: 12px;
  color: #6c757d;
  line-height: 1.5;
}

.actions-description {
  margin-top: 16px;
  padding: 16px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
}

.action-desc-item {
  font-size: 13px;
  color: #495057;
  line-height: 1.8;
  margin-bottom: 8px;
}

.action-desc-item:last-child {
  margin-bottom: 0;
}

.action-desc-item strong {
  color: #333;
  font-weight: 600;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
